import { createBrowserRouter, type LoaderFunctionArgs, Navigate, redirect, type RouteObject } from "react-router";

import { AppShell } from "@/layouts/AppShell";
import { RouteErrorPage } from "@/pages/RouteErrorPage";
import { withPreviewRoutes } from "@/features/operational-preview/routes";
import { withFlowRoutes } from "@/features/operational-flow/routes";

/** Calados o Fundidos en el tablero único de producción (Ola A §8.9): se conservan los demás parámetros. */
const alTableroUnico =
  (linea: "CA" | "FU") =>
  ({ request }: LoaderFunctionArgs) => {
    const q = new URL(request.url).searchParams;
    q.set("linea", linea);
    return redirect(`/produccion/lineas/BA?${q.toString()}`);
  };

/**
 * Rutas de la aplicación. Cada pantalla se descarga solo cuando se abre (`lazy`), para que
 * el celular de planta no cargue lo de gerencia. Las de cada módulo vivirán en
 * `src/features/<modulo>/`.
 */
export const routes: RouteObject[] = [
  {
    element: <AppShell />,
    errorElement: <RouteErrorPage />,
    // Lo que se ve mientras se descarga la primera pantalla.
    hydrateFallbackElement: <div aria-busy="true" className="min-h-dvh" />,
    children: [
      { path: "bienvenida", lazy: async () => ({ Component: (await import("@/features/bienvenida/BienvenidaPage")).BienvenidaPage }) },
      {
        index: true,
        lazy: async () => ({ Component: (await import("@/pages/HomePage")).HomePage }),
      },
      /**
       * **El Cotizador vive en Comercial › Cotizaciones desde el 11-oct** (plan del owner): se entra
       * con «Nueva cotización», abriendo un borrador o desde el acceso directo «Cotizador de baldosa»
       * del menú (13-oct), que lleva a esa misma dirección. Las
       * direcciones viejas siguen abriéndolo: están en marcadores y en mensajes. `/cotizador` era el
       * cotizador de una pieza, que el owner quitó el 11-oct; hoy lleva al Cotizador.
       */
      { path: "cotizador", element: <Navigate to="/comercial/cotizaciones/cotizador" replace /> },
      { path: "cotizador-baldosa", element: <Navigate to="/comercial/cotizaciones/cotizador" replace /> },
      {
        path: "ventas",
        lazy: async () => ({ Component: (await import("@/features/ventas/VentasPage")).VentasPage }),
      },
      /**
       * Las direcciones anteriores de Pedidos (27-sep-2026).
       *
       * Los pedidos dejaron de ser sección propia y viven dentro de Producción, dentro de su
       * línea. Pero `/pedidos/<id>` está en correos, en mensajes de WhatsApp y en el historial
       * del navegador de quien lleva la planta.
       *
       * **No se borran.** Un enlace que deja de funcionar no dice «esto se movió», dice «esto se
       * rompió» — y quien lo ve una vez deja de fiarse del programa para lo siguiente.
       */
      { path: "pedidos", element: <Navigate to="/produccion/pedidos" replace /> },
      // El pedido tiene un solo detalle (#59): la dirección vieja lleva a él, sin montar otra pantalla.
      { path: "pedidos/:orderId", loader: ({ params }) => redirect(`/comercial/flujo/${params.orderId}`) },
      {
        // Validar (Ola 2, punto 13; temporal): lo que hay que confirmar en planta, por área.
        path: "validar",
        lazy: async () => ({ Component: (await import("@/features/validar/ValidarPage")).ValidarPage }),
      },
      {
        // Pendientes (#29, #30): lo operativo que alguien tiene que hacer, por área.
        path: "pendientes",
        lazy: async () => ({ Component: (await import("@/features/pendientes/PendientesPage")).PendientesPage }),
      },
      {
        // Mejoras (M13): lo que la gente grabó con «Grabar mejora». Solo el owner (`mejoras.ver`).
        path: "mejoras",
        lazy: async () => ({ Component: (await import("@/features/mejoras/MejorasPage")).MejorasPage }),
      },
      {
        // Lo que hay en el estante. Sus precios son los que alimentan cada receta.
        path: "bodega",
        lazy: async () => ({ Component: (await import("@/features/bodega/StockPage")).StockPage }),
      },
      {
        // Comercial: hacia fuera. Terceros y cotizaciones, en ese orden.
        path: "comercial",
        lazy: async () => ({ Component: (await import("@/features/comercial/ComercialLayout")).ComercialLayout }),
        children: [
          { index: true, element: <Navigate to="terceros" replace /> },
          // La pantalla 04 se juntó con Pedidos (10). La dirección antigua conserva sus enlaces.
          {
            path: "resumen",
            loader: ({ request }) => redirect(`/comercial/flujo${new URL(request.url).search}`),
          },
          {
            path: "terceros",
            lazy: async () => ({ Component: (await import("@/features/comercial/TercerosPage")).TercerosPage }),
          },
          {
            // La hoja de vida del cliente: la raíz del árbol cliente → obras → cotizaciones →
            // pedidos. Cuelga de «terceros» y no de una entrada propia del menú porque es la
            // ficha de un tercero, no otra sección.
            path: "terceros/:customerId",
            lazy: async () => ({
              Component: (await import("@/features/comercial/CustomerLifePage")).CustomerLifePage,
            }),
          },
          {
            // La LISTA pasa a ser la pantalla principal de Cotizaciones. Antes esta dirección era
            // el formulario que armaba una cotización y la subía a Siigo; el owner decidió lo
            // contrario (26-sep-2026): la cotización se crea y se guarda aquí —para poder llevar
            // imágenes— y a Siigo se le manda una copia después.
            path: "cotizaciones",
            lazy: async () => ({ Component: (await import("@/features/comercial/QuotationsPage")).QuotationsPage }),
          },
          {
            // La ruta histórica informa la restricción de solo lectura y enlaza al ERP (D-053).
            path: "cotizaciones/siigo",
            lazy: async () => ({ Component: (await import("@/features/comercial/QuotationPage")).QuotationPage }),
          },
          {
            /**
             * **Traer de Siigo** (parte 2.1 de la beta): la cotización ya está escrita en Siigo y de
             * ahí nace el pedido. Va en Comercial y no en Cotizaciones porque lo que produce es un
             * pedido, no una cotización más: quien la abre viene con un número en la mano.
             */
            path: "traer-de-siigo",
            lazy: async () => ({
              Component: (await import("@/features/comercial/TraerDeSiigoPage")).TraerDeSiigoPage,
            }),
          },
          {
            /**
             * **El Cotizador**, a pantalla completa (`FullScreenApp`): lo abre «Nueva cotización». Va
             * antes de la ruta con parámetro porque es un segmento literal, como «nueva».
             */
            path: "cotizaciones/cotizador",
            lazy: async () => ({ Component: (await import("@/features/cotizador/TileQuotePage")).TileQuotePage }),
          },
          {
            // Un borrador abierto en el Cotizador: con su dibujo, o con su lista de líneas.
            path: "cotizaciones/:quoteId/cotizador",
            lazy: async () => ({ Component: (await import("@/features/cotizador/DraftQuotePage")).DraftQuotePage }),
          },
          {
            // La cotización manual, línea por línea. Antes de la ruta con parámetro: si no, «nueva»
            // se leería como un id. Se llega con «Nueva cotización» sin `cotizador.ver`; en el
            // Cotizador, lo que no está en el catálogo es «Añadir ítem › Ítem manual» (14-oct).
            path: "cotizaciones/nueva",
            lazy: async () => ({ Component: (await import("@/features/comercial/NewQuotePage")).NewQuotePage }),
          },
          {
            // Las remisiones: la entrega de una cotización o un pedido, total o por partes.
            path: "remisiones",
            lazy: async () => ({ Component: (await import("@/features/comercial/remisiones/RemisionesPage")).RemisionesPage }),
          },
          {
            path: "cotizaciones/:quoteId",
            lazy: async () => ({
              Component: (await import("@/features/comercial/QuoteDetailPage")).QuoteDetailPage,
            }),
          },
        ],
      },
      {
        path: "finanzas",
        lazy: async () => ({ Component: (await import("@/features/finanzas/FinanceLayout")).FinanceLayout }),
        children: [
          { index: true, element: <Navigate to="deudas" replace /> },
          { path: "deudas", lazy: async () => ({ Component: (await import("@/features/finanzas/DebtsPage")).DebtsPage }) },
          { path: "deudas/:id", lazy: async () => ({ Component: (await import("@/features/finanzas/DebtsPage")).DebtsPage }) },
          { path: "pagos", lazy: async () => ({ Component: (await import("@/features/finanzas/PaymentsPage")).PaymentsPage }) },
        ],
      },
      {
        path: "costos",
        lazy: async () => ({ Component: (await import("@/features/cost-base/CostsLayout")).CostsLayout }),
        children: [
          { index: true, element: <Navigate to="resumen" replace /> },
          {
            path: "resumen",
            lazy: async () => ({ Component: (await import("@/features/cost-base/SummaryPage")).SummaryPage }),
          },
          {
            path: "referencias",
            lazy: async () => ({ Component: (await import("@/features/cost-base/BaseCostPage")).BaseCostPage }),
          },
          // Dirección anterior a «Referencias de producto» (24-sep-2026).
          { path: "baldosa", element: <Navigate to="/costos/referencias" replace /> },
          {
            path: "materia-prima",
            lazy: async () => ({ Component: (await import("@/features/cost-base/MaterialsPage")).MaterialsPage }),
          },
          {
            path: "mano-de-obra",
            lazy: async () => ({ Component: (await import("@/features/cost-base/DirectLaborPage")).DirectLaborPage }),
          },
          {
            path: "gastos",
            lazy: async () => ({ Component: (await import("@/features/cost-base/ExpensesPage")).ExpensesPage }),
          },
          {
            path: "activos",
            lazy: async () => ({ Component: (await import("@/features/activos/AssetsPage")).AssetsPage }),
          },
          {
            path: "financieros",
            lazy: async () => ({ Component: (await import("@/features/cost-base/CreditsPage")).CreditsPage }),
          },
          // Pestañas anteriores a la estructura de costos (17-sep-2026, D-034).
          { path: "materias-primas", element: <Navigate to="/costos/materia-prima" replace /> },
          { path: "personal", element: <Navigate to="/costos/mano-de-obra" replace /> },
          // Pestañas anteriores a «Gastos administrativos» (17-sep-2026).
          { path: "cif", element: <Navigate to="/costos/gastos" replace /> },
          { path: "gastos-operacion", element: <Navigate to="/costos/gastos" replace /> },
          { path: "creditos", element: <Navigate to="/costos/financieros" replace /> },
        ],
      },
      {
        /**
         * **Nómina** (contrato del 12-oct-2026): la semana persona × día, la ficha de cada persona, las
         * novedades, los cortes con su liquidación y las reglas de bonificación. Costos › Mano de obra
         * no cambia.
         */
        path: "nomina",
        lazy: async () => ({ Component: (await import("@/features/nomina/NominaLayout")).NominaLayout }),
        children: [
          { index: true, lazy: async () => ({ Component: (await import("@/features/nomina/SemanaPage")).SemanaPage }) },
          { path: "persona/:employeeId", lazy: async () => ({ Component: (await import("@/features/nomina/PersonaPage")).PersonaPage }) },
          { path: "novedades", lazy: async () => ({ Component: (await import("@/features/nomina/NovedadesPage")).NovedadesPage }) },
          { path: "cortes", lazy: async () => ({ Component: (await import("@/features/nomina/CortesPage")).CortesPage }) },
          { path: "cortes/:periodId", lazy: async () => ({ Component: (await import("@/features/nomina/LiquidacionPage")).LiquidacionPage }) },
          { path: "bonificacion", lazy: async () => ({ Component: (await import("@/features/nomina/BonificacionPage")).BonificacionPage }) },
          { path: "calendario", lazy: async () => ({ Component: (await import("@/features/nomina/CalendarioPage")).CalendarioPage }) },
        ],
      },
      // Direcciones anteriores a la sección Costos (17-sep-2026).
      { path: "insumos", element: <Navigate to="/costos/materia-prima" replace /> },
      { path: "personal", element: <Navigate to="/costos/mano-de-obra" replace /> },
      { path: "gastos", element: <Navigate to="/costos/gastos" replace /> },
      {
        path: "configuracion",
        element: <Navigate to="/configuracion/sistema" replace />,
      },
      {
        path: "configuracion/accesos",
        lazy: async () => ({ Component: (await import("@/features/configuracion/AccesosPage")).AccesosPage }),
      },
      {
        path: "configuracion/accesos/permisos",
        lazy: async () => ({ Component: (await import("@/features/configuracion/AccesosPage")).AccesosPage }),
      },
      {
        path: "configuracion/accesos/tablero",
        lazy: async () => ({ Component: (await import("@/features/configuracion/TableroPorCargoPage")).TableroPorCargoPage }),
      },
      {
        path: "perfil",
        lazy: async () => ({ Component: (await import("@/features/acceso/PerfilPage")).PerfilPage }),
      },
      {
        path: "avisos",
        lazy: async () => ({ Component: (await import("@/features/avisos/InboxPage")).InboxPage }),
      },
      {
        path: "configuracion/accesos/avisos",
        lazy: async () => ({ Component: (await import("@/features/avisos/PoliciesPage")).PoliciesPage }),
      },
      {
        path: "configuracion/sistema",
        lazy: async () => ({ Component: (await import("@/pages/SystemStatusPage")).SystemStatusPage }),
      },
      {
        // El producto de Siigo de cada línea con que sale la copia de una cotización (contrato
        // «Cotización con el mismo número», 13-oct). Lo edita `configuracion.gestionar`.
        path: "configuracion/productos-siigo",
        lazy: async () => ({ Component: (await import("@/features/configuracion/ProductosSiigoPage")).ProductosSiigoPage }),
      },
      {
        // Parámetros (0182): los números del ERP que el owner cambia sin programar.
        path: "configuracion/parametros",
        lazy: async () => ({ Component: (await import("@/features/configuracion/ParametrosPage")).ParametrosPage }),
      },
      {
        // Interruptores (0182): lo nuevo sube apagado y el owner lo enciende.
        path: "configuracion/interruptores",
        lazy: async () => ({ Component: (await import("@/features/configuracion/InterruptoresPage")).InterruptoresPage }),
      },
      {
        // «Cambios»: la bitácora entera, sin el embudo de un pedido. Hija de `configuracion` en la
        // dirección porque comparte su permiso, pero pantalla aparte: se entra por una pregunta.
        path: "configuracion/cambios",
        lazy: async () => ({ Component: (await import("@/features/configuracion/CambiosPage")).CambiosPage }),
      },
      {
        // Producción: la planta por dentro. Es una escalera —resumen → línea → panel del pedido →
        // ficha completa— y cada escalón enseña solo lo suyo. La navegación vive en el menú lateral
        // (ver navigation.ts); `ProduccionLayout` ya no dibuja nada.
        path: "produccion",
        lazy: async () => ({
          Component: (await import("@/features/produccion/ProduccionLayout")).ProduccionLayout,
        }),
        children: [
          /**
           * **La entrada depende de quién entre**, y por eso no es un `Navigate` fijo.
           *
           * Llevaba siempre al resumen de planta, que para José —que prepara el color y no ve la
           * planta— era un 403 en la puerta de su propia sección. Ver `EntradaDeProduccion`.
           */
          {
            index: true,
            lazy: async () => ({
              Component: (await import("@/features/produccion/EntradaDeProduccion")).EntradaDeProduccion,
            }),
          },
          /**
           * **La entrada a Producción: el tablero de las cinco líneas.**
           *
           * Antes se entraba al cronograma, que contesta «¿quién está en qué máquina esta semana?».
           * Esa no es la primera pregunta de la mañana: la primera es «¿cómo va todo?».
           */
          {
            path: "resumen",
            lazy: async () => ({
              Component: (await import("@/features/produccion/TableroPage")).TableroPage,
            }),
          },
          /**
           * **Planificación: el cronograma y el calendario, juntos.**
           *
           * Contestaban la misma pregunta desde dos lados —«qué prensa está libre el jueves» y «qué
           * se entrega este mes»— y estaban en dos sitios, así que había que saber de antemano cuál
           * de los dos mirar. `/produccion/cronograma` sigue funcionando y lleva aquí: una dirección
           * que alguien tenga guardada no puede dejar de abrir nada.
           */
          {
            path: "planificacion",
            lazy: async () => ({
              Component: (await import("@/features/produccion/PlanificacionPage")).PlanificacionPage,
            }),
          },
          /** **Configuración: qué se le descuenta al artesano** (0151, owner #72). */
          {
            path: "configuracion",
            lazy: async () => ({
              Component: (await import("@/features/produccion/configuracion/ConfiguracionPage")).ConfiguracionPage,
            }),
          },
          { path: "metas", element: <Navigate to="/costos/referencias?linea=tile" replace /> },
          /**
           * **Programación viva: el bloque, la cola y las dos fechas.**
           *
           * Acepta `?bloque=<id>`, y eso no es un adorno: el cronograma y la ficha de un pedido
           * abren un bloque concreto sin tener que saber nada de esta pantalla, y el enlace se pega
           * en un chat y lleva al mismo sitio.
           *
           * **Fuera del menú desde L-7, viva en el enrutado.** Como pantalla a la que entrar «a ver
           * qué hay» repetía el tablero; como dirección a la que llegar con un bloque en la mano es
           * lo que sostiene esos enlaces, y matarla haría que un enlace de hace dos semanas
           * contestara «pantalla no encontrada» — que se lee como que el programa se rompió. Lo
           * único propio que tenía, lo pausado, se ve en la columna de su prensa.
           */
          {
            path: "programacion",
            lazy: async () => ({
              Component: (await import("@/features/produccion/EntradaDeBloqueAnterior")).EntradaDeBloqueAnterior,
            }),
          },
          { path: "cronograma", element: <Navigate to="/produccion/planificacion" replace /> },
          { path: "calendario", element: <Navigate to="/produccion/planificacion" replace /> },
          /**
           * **Turnos y personal: las horas extra y la jornada.**
           *
           * El mismo tema visto dos veces: la jornada dice hasta qué hora es normal quedarse, y
           * desde esa hora empieza el recargo. Un solo reloj (0055) y un solo sitio.
           */
          {
            path: "turnos",
            loader: () => redirect("/nomina/novedades#horas-extra-planta"),
          },
          /**
           * **Producción de Baldosa = el tablero** (contrato `tablero-produccion.md`, ola 4, §9-D1): la
           * cola guardada por artesano con sus fechas del servidor y sus cinco pestañas. Literal: va
           * **antes** que `lineas/:lineCode` y gana porque React Router puntúa un segmento escrito por
           * encima de un parámetro. Las acciones del buscador las atiende el tablero mismo:
           * `?accion=parte` abre «Ingresar producción» y `?accion=programar`, el tablero.
           */
          {
            path: "lineas/BA",
            lazy: async () => ({
              Component: (await import("@/features/produccion/tablero/TableroPage")).TableroPage,
            }),
          },
          /**
           * Las direcciones de antes siguen en enlaces y llevan al tablero: el prototipo (`simple`), el
           * tablero de las olas 1–3 (`tablero`) y la pantalla anterior de bloques (`anterior`), que se
           * retiró el 19-oct por decisión del owner.
           */
          { path: "lineas/BA/anterior", element: <Navigate to="/produccion/lineas/BA" replace /> },
          { path: "lineas/BA/simple", element: <Navigate to="/produccion/lineas/BA" replace /> },
          { path: "lineas/BA/tablero", element: <Navigate to="/produccion/lineas/BA" replace /> },
          /**
           * **Conciliar producción** (D-058): meter al ERP un pedido que ya corre en planta. App a
           * pantalla completa. Segmento literal, antes de `lineas/:lineCode`.
           */
          {
            path: "lineas/BA/conciliar",
            lazy: async () => ({
              Component: (await import("@/features/produccion/baldosa/conciliar/ConciliarProduccion")).ConciliarProduccion,
            }),
          },
          /**
           * **Planilla de conciliación** (contrato `conciliacion-planillas.md` §A): todas las líneas
           * en una tabla tipo Excel, con vista previa, descarga y subida del Excel.
           */
          {
            path: "conciliacion",
            lazy: async () => ({
              Component: (await import("@/features/produccion/conciliacion-planilla/PlanillaDeConciliacion")).PlanillaDeConciliacion,
            }),
          },
          /**
           * **Producción por línea** (contrato del 12-oct): Cerámicos tiene su tablero de taller y Pegantes su
           * pantalla de tandas. Literales, antes de `lineas/:lineCode`, así que ganan igual que `lineas/BA`.
           * `LineaPage` se conserva sin entrada para estas (S-17: decide el owner si se borra).
           *
           * **Calados y Fundidos viven en el tablero único** (Ola A 25-oct §8.9; Ola C D5): `lineas/CA` y
           * `lineas/FU` (y `calados`, `fundidos`, más abajo) solo redirigen a `/produccion/lineas/BA?linea=CA|FU`
           * con los demás parámetros (`accion`…), para los enlaces guardados. Su pantalla de taller se retiró en la
           * Ola C: `LineaTallerPage` es solo de Cerámicos.
           */
          { path: "lineas/CA", loader: alTableroUnico("CA") },
          { path: "lineas/FU", loader: alTableroUnico("FU") },
          {
            path: "lineas/CE",
            lazy: async () => ({
              Component: (await import("@/features/produccion/por-linea/LineaTallerPage")).LineaTallerPage,
            }),
          },
          /**
           * **Pegantes → NECA Labs · Mezclas › Pegantes** (P14 ronda 4, H6 ronda 5): el menú lleva al
           * segmento Pegantes de Labs, donde José hace las tandas. La pantalla de tandas de Pegante se
           * conserva en `lineas/PG/tandas`, sin menú ni enlace desde Labs; solo llega a ella una acción
           * del buscador («Nueva tanda · Pegante», `?accion=`).
           */
          {
            path: "lineas/PG",
            loader: ({ request }) => {
              const query = new URL(request.url).search;
              return redirect(new URLSearchParams(query).has("accion") ? `/produccion/lineas/PG/tandas${query}` : "/formulacion/labs?vista=mezclas&tipo=pegantes");
            },
          },
          {
            path: "lineas/PG/tandas",
            lazy: async () => ({
              Component: (await import("@/features/produccion/por-linea/pegante/PegantePage")).PegantePage,
            }),
          },
          {
            path: "lineas/:lineCode",
            lazy: async () => ({ Component: (await import("@/features/produccion/LineaPage")).LineaPage }),
          },
          /**
           * **El nombre que dice el menú, también como dirección.**
           *
           * El menú dice «Baldosa» y la dirección es `/produccion/lineas/BA`: quien lea la pantalla y
           * escriba la dirección a mano teclea `/produccion/baldosa` y se lleva un «no encontrada».
           * No es una ruta vieja que alguien tenga guardada — nunca existió — es la que la propia
           * pantalla invita a escribir. Cuesta cinco líneas y ahorra un callejón.
           */
          { path: "baldosa", element: <Navigate to="/produccion/lineas/BA" replace /> },
          { path: "calados", loader: alTableroUnico("CA") },
          { path: "pegantes", element: <Navigate to="/produccion/lineas/PG" replace /> },
          { path: "fundidos", loader: alTableroUnico("FU") },
          { path: "ceramicos", element: <Navigate to="/produccion/lineas/CE" replace /> },
          {
            path: "pedidos",
            lazy: async () => ({ Component: (await import("@/features/pedidos/OrdersPage")).OrdersPage }),
          },
          // El detalle único vive en Comercial (#59); esta dirección sigue en enlaces y mensajes.
          { path: "pedidos/:orderId", loader: ({ params }) => redirect(`/comercial/flujo/${params.orderId}`) },
          /**
           * **La hoja del día** (fase C del plan del tablero, 30-sep): la hoja de hidratación del Excel
           * del owner, en pantalla. Todos los que trabajaron ese día —también en lo gris—, la cantidad
           * hoy y las malas al día siguiente en la misma fila, y los totales al pie.
           *
           * Se queda con la dirección de «Parte del día» porque **la reemplaza**: quien tenga el enlace
           * guardado llega a lo que vino a hacer.
           */
          { path: "parte", element: <Navigate to="/produccion/lineas/BA" replace /> },
          /**
           * **Formulación: la pantalla de José** (plan de la lisa 20×20, fase 4).
           *
           * Es el tercer escalón de la cascada y hasta ahora no tenía pantalla: José recibía el color
           * por WhatsApp o por la hoja de Elkin. Aquí ve lo que le pidieron, para qué día y para qué
           * obra, y anota lo que pesó de verdad.
           */
          {
            path: "formulacion",
            loader: () => redirect("/formulacion"),
          },
          /**
           * **La página de un lote de color.**
           *
           * Tiene dos caras y la decide el permiso, no la dirección: quien prepara el color ve lo que
           * costó; Comercial, que entra desde su pedido con `?pedido=`, ve el mismo lote sin un peso.
           * Una sola dirección para las dos porque es el mismo lote — dos páginas del mismo hecho
           * acaban diciendo dos cosas.
           */
          {
            path: "lotes/:mixId",
            lazy: async () => ({ Component: (await import("@/features/produccion/LotePage")).LotePage }),
          },
          /**
           * **Los partes que no están atados a ningún pedido.**
           *
           * 64 del 1 al 15 de septiembre: piezas hechas de verdad que no suben el avance de ninguna
           * obra. La pantalla es de solo lectura y lo dice: no hay con qué atarlas todavía.
           */
          { path: "partes-sueltos", element: <Navigate to="/produccion/lineas/BA" replace /> },
          /**
           * **La pantalla de Calidad se fue** (L-3 de la revisión de salud).
           *
           * Contaba lo malo con la forma vieja —superficie, tercera, despegadas, rotas— mientras la
           * hoja del día y el panel por prensa cuentan la del Excel del owner. Dos formas vivas de
           * contar lo mismo dan dos cifras distintas, y la que se mira acaba siendo la que esté más a
           * mano.
           *
           * Las dos direcciones siguen contestando, llevando a donde ahora se cuenta: un enlace
           * guardado o un marcador no puede acabar en «no encontrado» sin decir dónde está lo que
           * buscaba.
           */
          { path: "calidad", element: <Navigate to="/produccion/lineas/BA" replace /> },
          { path: "seleccion", element: <Navigate to="/produccion/lineas/BA" replace /> },
          { path: "ajustes", loader: () => redirect("/nomina#jornada-equipo") },
          { path: "horas-extra", loader: () => redirect("/nomina/novedades#horas-extra-planta") },
        ],
      },
      {
        path: "sistema",
        element: <Navigate to="/configuracion/sistema" replace />,
      },
      {
        path: "*",
        lazy: async () => ({ Component: (await import("@/pages/NotFoundPage")).NotFoundPage }),
      },
    ],
  },
];

export const createRouter = () => createBrowserRouter(import.meta.env.VITE_OPERATIONAL_PREVIEW === "true" ? withPreviewRoutes(routes) : withFlowRoutes(routes));
